import { gql, type TypedDocumentNode } from '@apollo/client';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ShieldButton } from '@sorare/core/src/atoms/buttons/ShieldButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PickerCard } from '#src/components/pickers/BoardPicker/PickerCard.tsx';
import { PickerHeader } from '#src/components/pickers/PickerHeader/index.tsx';

import type { CompetitionPicker_so5LeaderboardGroup } from './__generated__/index.graphql.ts';

const GroupIcon = styled.img`
  width: var(--triple-unit);
  height: var(--triple-unit);
  object-fit: contain;

  &.md {
    width: var(--quadruple-unit);
    height: var(--quadruple-unit);
    margin-top: var(--intermediate-unit);
  }
`;

const GridIcon = styled.img`
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
  object-fit: contain;
`;

const Grid = styled(Horizontal).attrs({ gap: 1, center: true })`
  flex-wrap: wrap;
  padding: var(--double-unit);

  @media ${laptopAndAbove} {
    min-width: 460px;
  }
`;

type FlatLeaderboard =
  CompetitionPicker_so5LeaderboardGroup['so5Leaderboards'][number] & {
    iconUrl: string;
  };

type Props = {
  groups: readonly CompetitionPicker_so5LeaderboardGroup[];
  selected?: string;
  onSelect: (slug: string) => void;
} & Pick<ComponentProps<typeof ShieldButton>, 'size'>;

export const CompetitionPicker = ({
  groups,
  selected,
  onSelect,
  size = 'sm',
}: Props) => {
  const leaderboards: FlatLeaderboard[] = groups.flatMap(g =>
    g.so5Leaderboards.map(l => ({ ...l, iconUrl: g.iconUrl }))
  );

  if (leaderboards.length <= 1) return null;

  const currentLeaderboard =
    leaderboards.find(l => l.slug === selected) ?? leaderboards[0];
  const { iconUrl } = currentLeaderboard;

  return (
    <Dropdown
      align="center"
      gap={8}
      label={props => (
        <ShieldButton
          type="button"
          aria-label={currentLeaderboard.so5LeaderboardType ?? undefined}
          size={size}
          icon={
            iconUrl ? (
              <GroupIcon
                className={size === 'md' ? 'md' : undefined}
                src={iconUrl}
                alt=""
                role="presentation"
              />
            ) : undefined
          }
          orientation="down"
          {...props}
        />
      )}
    >
      {({ closeDropdown }) => (
        <>
          <PickerHeader onClose={closeDropdown}>
            <FormattedMessage defaultMessage="Choose Competition" />
          </PickerHeader>
          <Grid>
            {leaderboards.map(leaderboard => (
              <PickerCard
                key={leaderboard.slug}
                selected={leaderboard.slug === currentLeaderboard.slug}
                onClick={() => {
                  onSelect(leaderboard.slug);
                  closeDropdown();
                }}
              >
                {!!leaderboard.iconUrl && (
                  <GridIcon
                    src={leaderboard.iconUrl}
                    alt={leaderboard.so5LeaderboardType ?? ''}
                  />
                )}
              </PickerCard>
            ))}
          </Grid>
        </>
      )}
    </Dropdown>
  );
};

CompetitionPicker.fragments = {
  so5LeaderboardGroup: gql`
    fragment CompetitionPicker_so5LeaderboardGroup on So5LeaderboardGroup {
      slug
      iconUrl
      so5Leaderboards {
        id
        slug
        so5LeaderboardType
        mainRarityType
      }
    }
  ` as TypedDocumentNode<CompetitionPicker_so5LeaderboardGroup>,
};
